// Chart presets for calorimeter test data, carried over from the HVAC lab
// dashboard Bluestar approved. The tab names, the channels each tab plots, and
// the unit-grouped Y axes are that dashboard's; changing them changes the
// approved look.

// Each channel is plotted on the axis for its physical quantity, so every
// temperature shares one °C axis and every power one W axis. Ten channels
// therefore need three or four axes, not ten.
export const AXIS_GROUPS = {
  Power: { label: 'Power (W)', unit: 'W', color: '#dc2626', side: 'left' },
  Voltage: { label: 'Voltage (V)', unit: 'V', color: '#92400e', side: 'left' },
  Current: { label: 'Current (A)', unit: 'A', color: '#7c3aed', side: 'left' },
  Temp: { label: 'Temp (°C)', unit: '°C', color: '#ea580c', side: 'left' },
  Press: { label: 'Pressure', unit: 'Pa', color: '#db2777', side: 'left' },
  AirFlow: { label: 'Air Flow', unit: 'm3/h', color: '#0891b2', side: 'left' },
  EER: { label: 'EER (W/W)', unit: 'W/W', color: '#1e40af', side: 'left' },
  Capa: { label: 'Capacity (W)', unit: 'W', color: '#00a000', side: 'right' },
  Percent: { label: '% & Others', unit: '%', color: '#65a30d', side: 'left' },
  Enthalpy: { label: 'Enthalpy (kJ/kg)', unit: 'kJ/kg', color: '#0d9488', side: 'right' },
  Energy: { label: 'Energy (kWh)', unit: 'kWh', color: '#6366f1', side: 'right' },
  Freq: { label: 'Frequency (Hz)', unit: 'Hz', color: '#475569', side: 'left' }
}

export const AXIS_ORDER = ['Temp', 'Power', 'Voltage', 'Current', 'AirFlow', 'EER', 'Press', 'Percent', 'Capa', 'Enthalpy', 'Energy', 'Freq']

export const TABS = ['PVC', 'EER/COP', 'VOLTAGE, CURRENT', 'TEMPERATURE', 'PERFORMANCE', 'CUSTOM']

// The tabs mirror the calorimeter's own viewer. Each entry matches a channel
// whose name starts with `match`; names vary slightly between rigs (ID/Indoor,
// OD/Outdoor), so both spellings are listed and the first present one wins.
export const TAB_PRESETS = {
  PVC: [
    { match: 'UUT Power', color: '#dc2626', dash: 'solid' },
    { match: 'UUT Capacity', color: '#f97316', dash: 'solid' },
    { match: 'ID Airflow', color: '#06b6d4', dash: 'solid' },
    { match: 'Indoor Airflow', color: '#06b6d4', dash: 'solid' }
  ],
  'EER/COP': [
    { match: 'EER', color: '#1e3a8a', dash: 'solid' },
    { match: 'COP', color: '#7c3aed', dash: 'solid' },
    { match: 'UUT Power', color: '#dc2626', dash: 'dash' },
    { match: 'UUT Capacity', color: '#f97316', dash: 'dash' }
  ],
  'VOLTAGE, CURRENT': [
    { match: 'UUT Power', color: '#dc2626', dash: 'solid' },
    { match: 'UUT Voltage', color: '#b45309', dash: 'solid' },
    { match: 'UUT Current', color: '#6d28d9', dash: 'solid' },
    { match: 'Power #1', color: '#fb923c', dash: 'dot' },
    { match: 'Power #2', color: '#ef4444', dash: 'dash' },
    { match: 'Voltage #1', color: '#92400e', dash: 'solid' },
    { match: 'Voltage #2', color: '#78350f', dash: 'dash' },
    { match: 'Current #1', color: '#7c3aed', dash: 'solid' },
    { match: 'Current #2', color: '#a855f7', dash: 'dash' },
    { match: 'OD UUT Power', color: '#f97316', dash: 'dot' },
    { match: 'Outdoor UUT Power', color: '#f97316', dash: 'dot' },
    { match: 'ID UUT Power', color: '#f59e0b', dash: 'dot' },
    { match: 'Indoor UUT Power', color: '#f59e0b', dash: 'dot' },
    { match: 'Power Factor', color: '#9333ea', dash: 'dot' }
  ],
  TEMPERATURE: [
    { match: 'ID Entering DB', color: '#06b6d4', dash: 'solid' },
    { match: 'Indoor Entering DB', color: '#06b6d4', dash: 'solid' },
    { match: 'ID Entering WB', color: '#2563eb', dash: 'dash' },
    { match: 'Indoor Entering WB', color: '#2563eb', dash: 'dash' },
    { match: 'ID Leaving DB', color: '#14b8a6', dash: 'solid' },
    { match: 'Indoor Leaving DB', color: '#14b8a6', dash: 'solid' },
    { match: 'ID Leaving WB', color: '#10b981', dash: 'dash' },
    { match: 'Indoor Leaving WB', color: '#10b981', dash: 'dash' },
    { match: 'OD Entering DB', color: '#f59e0b', dash: 'solid' },
    { match: 'Outdoor Entering DB', color: '#f59e0b', dash: 'solid' },
    { match: 'OD Entering WB', color: '#e11d48', dash: 'dash' },
    { match: 'Outdoor Entering WB', color: '#e11d48', dash: 'dash' }
  ],
  PERFORMANCE: [
    { match: 'UUT Power', color: '#dc2626', dash: 'solid' },
    { match: 'UUT Capacity', color: '#00a000', dash: 'solid' },
    { match: 'COP', color: '#1e3a8a', dash: 'dash' },
    { match: 'ID Entering DB', color: '#06b6d4', dash: 'solid' },
    { match: 'Indoor Entering DB', color: '#06b6d4', dash: 'solid' },
    { match: 'ID Leaving DB', color: '#14b8a6', dash: 'solid' },
    { match: 'OD Entering DB', color: '#f59e0b', dash: 'solid' },
    { match: 'Outdoor Entering DB', color: '#f59e0b', dash: 'solid' },
    { match: 'ID Airflow', color: '#0891b2', dash: 'solid' },
    { match: 'Indoor Airflow', color: '#0891b2', dash: 'solid' },
    { match: 'ID Capacity', color: '#84cc16', dash: 'solid' },
    { match: 'Indoor Capacity', color: '#84cc16', dash: 'solid' },
    { match: 'ID Sensible', color: '#166534', dash: 'dash' },
    { match: 'ID Latent', color: '#059669', dash: 'dot' }
  ]
}

export const CUSTOM_COLORS = [
  '#2563eb', '#dc2626', '#059669', '#d97706', '#7c3aed', '#06b6d4',
  '#ec4899', '#84cc16', '#f59e0b', '#6366f1', '#14b8a6', '#f43f5e',
  '#8b5cf6', '#22c55e', '#ef4444', '#0ea5e9', '#a855f7', '#10b981',
  '#be185d', '#ca8a04', '#0d9488', '#4f46e5', '#e11d48', '#0284c7'
]

// The order the CUSTOM parameter picker lists channel groups in.
export const GROUP_ORDER = ['UUT', 'Power Meter', 'Power Meter A', 'Power Meter B', 'Indoor', 'Outdoor', 'Others', 'Indoor TC', 'Outdoor TC', 'Original']

// Channels ({ group, ... }) gathered under the rig's own group names, in
// GROUP_ORDER and then in the order other groups first appear
export const groupChannels = (channels) => {
  const groups = {}
  const seen = []
  channels.forEach(c => {
    const name = c.group || 'Other'
    if (!groups[name]) {
      groups[name] = []
      seen.push(name)
    }
    groups[name].push(c)
  })
  const order = GROUP_ORDER.filter(g => groups[g]).concat(seen.filter(g => !GROUP_ORDER.includes(g)))
  return order.map(name => ({ name, channels: groups[name] }))
}

// Which axis a channel belongs on, from its name first (capacity and EER carry
// power-like units but are read against their own scales) and then its unit.
export const getAxisGroup = (name, unit) => {
  if (!unit) return 'Percent'
  const u = unit.toLowerCase()
  if (name.includes('Capacity') || name.includes('Sensible') || name.includes('Latent')) return 'Capa'
  if (name.includes('EER') || name.includes('COP')) return 'EER'
  if (u === 'w' || u === 'btu') return 'Power'
  if (u === 'v') return 'Voltage'
  if (u === 'a') return 'Current'
  if (u === 'w/w') return 'EER'
  if (u.includes('°') || u.includes('c')) return 'Temp'
  if (u === 'pa' || u === 'mpa' || u === 'psi') return 'Press'
  if (u.includes('m3') || u === 'cfm' || u === 'm/s') return 'AirFlow'
  if (u === '%') return 'Percent'
  if (u.includes('kj')) return 'Enthalpy'
  if (u === 'kwh') return 'Energy'
  if (u === 'hz') return 'Freq'
  if (u.includes('kg')) return 'AirFlow'
  return 'Percent'
}

// Resolves a tab's preset against the channels this test actually recorded.
// A preset that finds fewer than three channels (a rig without, say, a second
// power meter) is topped up with the first recorded channels, so no tab ever
// opens empty.
//
// A finished test matches only channels that ever read non-zero, which hides
// unwired inputs. A live test matches every channel: in its first minutes the
// unit is still starting and its power genuinely reads zero, and that flat
// line is the start of the curve, not an unwired input.
export const resolvePreset = (tabName, dataset, { live = false } = {}) => {
  const defs = TAB_PRESETS[tabName]
  if (!defs || !dataset) return []

  const active = dataset.activeKeys
  const pool = live ? dataset.keys : active
  const resolved = []
  const used = new Set()

  for (const def of defs) {
    const key = pool.find(k => !used.has(k) && dataset.channels[k].name.startsWith(def.match))
    if (key) {
      used.add(key)
      resolved.push({ key, color: def.color, dash: def.dash })
    }
  }

  if (resolved.length < 3) {
    let ci = 0
    for (const key of active.filter(k => !used.has(k)).slice(0, 10)) {
      resolved.push({ key, color: CUSTOM_COLORS[ci++ % CUSTOM_COLORS.length], dash: 'solid' })
    }
  }
  return resolved
}

// Plotly layout with one free-floating Y axis per axis group in use, left-side
// groups stacked outward from the plot's left edge and right-side groups from
// its right edge, as in the approved dashboard.
// `compact` is for phone-width screens, where one strip of tick labels per
// axis would leave no room for the plot: only the first axis is labelled,
// the others share its edge unlabelled, and the legend is left to the values
// panel. Tapping the chart still shows every value, and the Y-axis toggles
// isolate one quantity with its own scale.
export const buildLayout = (orderedGroups, { compact = false } = {}) => {
  const leftAxes = orderedGroups.filter(g => AXIS_GROUPS[g].side === 'left')
  const rightAxes = orderedGroups.filter(g => AXIS_GROUPS[g].side === 'right')

  const axisStep = compact ? 0 : 0.06
  const domainLeft = compact ? 0 : Math.min(leftAxes.length * axisStep + 0.02, 0.4)
  const domainRight = compact ? 1 : Math.max(1 - rightAxes.length * axisStep - 0.02, 0.6)

  const layout = {
    margin: compact ? { l: 44, r: 8, t: 12, b: 36 } : { l: 10, r: 10, t: 20, b: 50 },
    showlegend: !compact,
    xaxis: {
      title: compact ? undefined : { text: 'Time', font: { size: 11 } },
      nticks: compact ? 4 : undefined,
      domain: [domainLeft, domainRight],
      gridcolor: '#e8e8e8',
      zeroline: false,
      type: 'date'
    },
    legend: { orientation: 'h', y: -0.22, font: { size: 10 }, tracegroupgap: 4 },
    plot_bgcolor: '#fafbfc',
    paper_bgcolor: '#fff',
    hovermode: 'x unified',
    font: { family: 'Segoe UI, system-ui, sans-serif', size: 11 },
    // Keeps the viewer's zoom and pan while new rows stream in; without it
    // every refresh would snap the chart back to the full range.
    uirevision: 'keep'
  }

  orderedGroups.forEach((grp, i) => {
    const g = AXIS_GROUPS[grp]
    const key = i === 0 ? 'yaxis' : `yaxis${i + 1}`
    const isRight = g.side === 'right'
    const position = isRight
      ? domainRight + 0.01 + rightAxes.indexOf(grp) * axisStep
      : domainLeft - 0.02 - leftAxes.indexOf(grp) * axisStep

    layout[key] = {
      title: compact ? undefined : { text: g.label, font: { size: 10, color: g.color } },
      tickfont: { color: g.color, size: 9 },
      showticklabels: !compact || i === 0,
      anchor: compact && i === 0 ? 'x' : 'free',
      side: isRight && !compact ? 'right' : 'left',
      position: compact ? 0 : Math.max(0, Math.min(1, position)),
      showgrid: i === 0,
      gridcolor: i === 0 ? '#e8e8e8' : undefined,
      zeroline: false
    }
    if (i > 0) layout[key].overlaying = 'y'
  })

  return layout
}
